import React, { useMemo, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { KpiCard } from './KpiCard';
import { FilterToolbar } from './FilterToolbar';
import { TeamMemberTable } from './TeamMemberTable';
import { NonAdminList } from './NonAdminList';
import { AccessRestricted } from './AccessRestricted';
import { TeamMember, Status, PaymentMethod, DateFilter, ValidationFilter } from '../types';
import { generateMockTeamMembers } from '../utils/mockData';
import { formatCurrency, dateFilterToLabel } from '../utils/formatting';
export function CommissionCenter() {
  const [allMembers] = useState<TeamMember[]>(() => generateMockTeamMembers(12));
  const [dateFilter, setDateFilter] = useState<DateFilter>(() => {
    const today = new Date();
    const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return `${prev.getFullYear()}-${(prev.getMonth() + 1).toString().padStart(2, '0')}`;
  });
  const [validationFilters, setValidationFilters] = useState<ValidationFilter[]>(['Validated', 'Unvalidated']);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [paymentMethodFilters, setPaymentMethodFilters] = useState<string[]>([]);
  const [commissionModelFilters, setCommissionModelFilters] = useState<string[]>([]);
  const [hideZeroCommission, setHideZeroCommission] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasAccess, setHasAccess] = useState(true);
  const [sortBy, setSortBy] = useState<string | null>('payroll');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setBy(column);
      setSortDirection(column === 'payroll' ? 'desc' : 'asc');
    }
  };
  const filteredAndSortedMembers = useMemo(() => {
    let filtered = [...allMembers];
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(m => m.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    // Status filter
    if (statusFilters.length > 0) {
      filtered = filtered.filter(m => m.status && statusFilters.includes(m.status));
    }
    // Payment method filter
    if (paymentMethodFilters.length > 0) {
      filtered = filtered.filter(m => m.preferredPaymentMethod && paymentMethodFilters.includes(m.preferredPaymentMethod));
    }
    // Commission model filter
    if (commissionModelFilters.length > 0) {
      filtered = filtered.filter(m => commissionModelFilters.includes(m.commissionModel));
    }
    // Hide zero commission
    if (hideZeroCommission) {
      filtered = filtered.filter(m => m.commission !== 0);
    }
    // Sort
    if (sortBy) {
      filtered.sort((a, b) => {
        let aVal: any;
        let bVal: any;
        switch (sortBy) {
          case 'name':
            aVal = a.name.toLowerCase();
            bVal = b.name.toLowerCase();
            break;
          case 'status':
            aVal = (a.status || '').toLowerCase();
            bVal = (b.status || '').toLowerCase();
            break;
          case 'paymentMethod':
            aVal = (a.preferredPaymentMethod || '').toLowerCase();
            bVal = (b.preferredPaymentMethod || '').toLowerCase();
            break;
          case 'payroll':
            aVal = a.totalPayrollAmount;
            bVal = b.totalPayrollAmount;
            break;
          default:
            return 0;
        }
        if (typeof aVal === 'string') {
          return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      });
    }
    // Overlay pay period label from selected date filter, and filter payPeriods to match
    const periodLabel = dateFilterToLabel(dateFilter);
    const matchesDateFilter = (date: Date): boolean => {
      if (!dateFilter) return true;
      if (dateFilter === 'YTD') {
        return date.getFullYear() === new Date().getFullYear();
      }
      if (dateFilter === 'Last Year') {
        return date.getFullYear() === new Date().getFullYear() - 1;
      }
      const match = /^(\d{4})-(\d{2})$/.exec(dateFilter);
      if (match) {
        const year = parseInt(match[1], 10);
        const monthIndex = parseInt(match[2], 10) - 1;
        return date.getFullYear() === year && date.getMonth() === monthIndex;
      }
      return true;
    };
    filtered = filtered.map(m => {
      const visiblePayPeriods = m.payPeriods.filter(pp => matchesDateFilter(pp.startDate));
      // Recompute counts so the badge / KPI tiles match what's visible
      // in the expanded Closed Loans / Referral Program tables.
      const paymentItemCount = visiblePayPeriods.reduce((sum, pp) => sum + pp.lineItems.filter(li => li.paymentType === 'Payment').length, 0);
      const referralPaymentItemCount = visiblePayPeriods.reduce((sum, pp) => sum + pp.lineItems.filter(li => li.paymentType === 'Referral Payment').length, 0);
      const totalPayrollAmount = visiblePayPeriods.reduce((sum, pp) => sum + pp.commissionAmount, 0);
      return {
        ...m,
        payPeriod: periodLabel,
        payPeriods: visiblePayPeriods,
        paymentItemCount,
        referralPaymentItemCount,
        totalPayrollAmount,
        commission: totalPayrollAmount
      };
    });
    return filtered;
  }, [allMembers, searchTerm, statusFilters, paymentMethodFilters, commissionModelFilters, hideZeroCommission, sortBy, sortDirection, dateFilter]);
  const kpiMetrics = useMemo(() => {
    const totalLoans = filteredAndSortedMembers.reduce((sum, m) => sum + m.paymentItemCount, 0);
    const totalReferrals = filteredAndSortedMembers.reduce((sum, m) => sum + m.referralPaymentItemCount, 0);
    const totalPayroll = filteredAndSortedMembers.reduce((sum, m) => sum + m.totalPayrollAmount, 0);
    const combinedTotal = totalLoans + totalReferrals;
    return [{
      label: 'Total Loans Funded',
      value: totalLoans,
      iconName: 'CheckCircle2' as const,
      color: 'blue',
      clickable: true,
      onClick: () => {
        console.log('Opening funded loans detail page');
        toast.info('Opening funded loans detail page');
      }
    }, {
      label: 'Total Closed Referral Loans',
      value: totalReferrals,
      iconName: 'Users' as const,
      color: 'purple',
      clickable: true,
      onClick: () => {
        console.log('Opening referral loans detail page');
        toast.info('Opening referral loans detail page');
      }
    }, {
      label: 'Combined Loans',
      value: combinedTotal,
      iconName: 'Layers' as const,
      color: 'amber'
    }, {
      label: 'Total Payroll Amount',
      value: formatCurrency(totalPayroll),
      iconName: 'DollarSign' as const,
      color: 'green'
    }];
  }, [filteredAndSortedMembers]);
  const handleUpdateStatus = (memberId: string, status: Status) => {
    const memberIndex = allMembers.findIndex(m => m.id === memberId);
    if (memberIndex !== -1) {
      allMembers[memberIndex].status = status;
    }
  };
  const handleUpdatePaymentMethod = (memberId: string, method: PaymentMethod) => {
    const memberIndex = allMembers.findIndex(m => m.id === memberId);
    if (memberIndex !== -1) {
      allMembers[memberIndex].preferredPaymentMethod = method;
    }
  };
  if (!hasAccess) {
    return <div className="min-h-screen bg-slate-50 p-6" data-id="element-8">
        <Toaster position="top-right" data-id="element-9" />
        <div className="max-w-[1600px] mx-auto" data-id="element-10">
          <div className="mb-6 flex justify-end" data-id="element-11">
            <button onClick={() => setHasAccess(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" data-id="element-12">
              Grant Access (Demo)
            </button>
          </div>
          <AccessRestricted data-id="element-13" />
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50 p-6" data-id="element-14">
      <Toaster position="top-right" data-id="element-15" />
      <div className="max-w-[1600px] mx-auto space-y-6" data-id="element-16">
        {/* Header with demo controls */}
        <div className="flex items-start justify-between" data-id="element-17">
          <div data-id="element-18">
            <h1 className="text-3xl font-bold text-slate-900 mb-2" data-id="element-19">
              Commission Center
            </h1>
            <p className="text-slate-600" data-id="element-20">
              Track loan funding, referrals, and payroll across your team.
            </p>
          </div>
          <div className="flex gap-2" data-id="element-21">
            <button onClick={() => setHasAccess(false)} className="px-4 py-2 bg-white text-slate-700 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50" data-id="element-22">
              Revoke Access (Demo)
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-23">
          {kpiMetrics.map((metric, index) => <KpiCard key={index} label={metric.label} value={metric.value} iconName={metric.iconName} color={metric.color} clickable={metric.clickable} onClick={metric.onClick} footnote={metric.footnote} data-id="element-24" />)}
        </div>

        {/* Filter Toolbar */}
        <FilterToolbar dateFilter={dateFilter} onDateFilterChange={setDateFilter} validationFilters={validationFilters} onValidationFilterChange={setValidationFilters} statusFilters={statusFilters} onStatusFiltersChange={setStatusFilters} paymentMethodFilters={paymentMethodFilters} onPaymentMethodFiltersChange={setPaymentMethodFilters} commissionModelFilters={commissionModelFilters} onCommissionModelFiltersChange={setCommissionModelFilters} hideZeroCommission={hideZeroCommission} onHideZeroCommissionChange={setHideZeroCommission} searchTerm={searchTerm} onSearchTermChange={setSearchTerm} data-id="element-25" />

        {/* Main Content */}
        <TeamMemberTable members={filteredAndSortedMembers} onUpdateStatus={handleUpdateStatus} onUpdatePaymentMethod={handleUpdatePaymentMethod} sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} data-id="element-26" />
      </div>
    </div>;
}